// ═══════════════════════════════════════════════════════════════
// Global Search — search across modules from anywhere
// ═══════════════════════════════════════════════════════════════

import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal, TextInput, Stack, Text, Group, Badge, Paper, Kbd,
} from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';

// Import stores for search
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useImpactStore } from '../../store/impactStore';
import { useShelterStore } from '../../store/shelterStore';
import { useDebrisOpsStore } from '../../store/debrisOpsStore';
import { useIntakeStore } from '../../store/intakeStore';

interface SearchResult {
  type: string;
  label: string;
  detail: string;
  route: string;
  color: string;
}

interface Props {
  opened: boolean;
  onClose: () => void;
}

export default function GlobalSearch({ opened, onClose }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  // Pull data from stores (already loaded by polling/mount)
  const { projects, organizations } = usePAGrantsStore();
  const { assessments } = useImpactStore();
  const { shelters, survivors } = useShelterStore();
  const { loadTickets } = useDebrisOpsStore();
  const { individuals } = useIntakeStore();

  // Reset on close
  useEffect(() => {
    if (!opened) setQuery('');
  }, [opened]);

  const results = useMemo<SearchResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const hits: SearchResult[] = [];
    const MAX = 20;

    // PA Projects
    for (const p of projects) {
      if (hits.length >= MAX) break;
      if (
        (p.projectTitle || '').toLowerCase().includes(q) ||
        (p.projectNumber || '').toLowerCase().includes(q) ||
        (p.projectAddress || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'PA Project',
          label: `${p.projectNumber} — ${p.projectTitle}`,
          detail: p.projectAddress || p.phase || '',
          route: `/pagrants/pa/projects/${p.Rid}`,
          color: 'blue',
        });
      }
    }

    // Organizations
    for (const o of organizations) {
      if (hits.length >= MAX) break;
      if (
        (o.orgName || '').toLowerCase().includes(q) ||
        (o.primaryEmail || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'Organization',
          label: o.orgName,
          detail: o.primaryEmail || o.orgType || '',
          route: `/pagrants/pa/applicants/${o.Rid}`,
          color: 'violet',
        });
      }
    }

    // Shelters
    for (const s of shelters) {
      if (hits.length >= MAX) break;
      if (
        (s.name || '').toLowerCase().includes(q) ||
        (s.address || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'Shelter',
          label: s.name,
          detail: `${s.address || ''} — ${s.currentPopulation}/${s.capacity}`,
          route: '/shelter/shelters',
          color: 'teal',
        });
      }
    }

    // Survivors
    for (const s of survivors) {
      if (hits.length >= MAX) break;
      const name = `${s.firstName || ''} ${s.lastName || ''}`.trim();
      if (
        name.toLowerCase().includes(q) ||
        (s.phone || '').includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'Survivor',
          label: name || 'Unknown',
          detail: s.shelterId ? `Shelter ID: ${s.shelterId}` : s.status || '',
          route: '/shelter/survivors',
          color: 'green',
        });
      }
    }

    // Intake Individuals
    for (const ind of individuals) {
      if (hits.length >= MAX) break;
      const name = `${ind.firstName || ''} ${ind.lastName || ''}`.trim();
      if (
        name.toLowerCase().includes(q) ||
        (ind.tagCode || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'Intake',
          label: name || 'Unknown',
          detail: ind.triage ? `Triage: ${ind.triage}` : ind.status || '',
          route: '/intake',
          color: 'orange',
        });
      }
    }

    // Impact Assessments
    for (const a of assessments) {
      if (hits.length >= MAX) break;
      if (
        (a.address || '').toLowerCase().includes(q) ||
        (a.facilityName || '').toLowerCase().includes(q) ||
        (a.assessorName || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'Assessment',
          label: a.address || a.facilityName || 'Unknown',
          detail: `${a.damageCategory || ''} — ${a.severity ? 'Severity ' + a.severity : ''}`,
          route: '/impact/assessments',
          color: 'red',
        });
      }
    }

    // Debris Load Tickets
    for (const t of loadTickets) {
      if (hits.length >= MAX) break;
      if (
        (t.ticketNumber || '').toLowerCase().includes(q) ||
        (t.truckNumber || '').toLowerCase().includes(q) ||
        (t.pickupAddress || '').toLowerCase().includes(q)
      ) {
        hits.push({
          type: 'Debris Ticket',
          label: t.ticketNumber || `Ticket #${t.id}`,
          detail: `Truck ${t.truckNumber || '?'} — ${t.status || ''}`,
          route: `/debris/tickets?search=${encodeURIComponent(t.ticketNumber || '')}`,
          color: 'yellow',
        });
      }
    }

    return hits;
  }, [query, projects, organizations, shelters, survivors, individuals, assessments, loadTickets]);

  const handleSelect = useCallback((result: SearchResult) => {
    navigate(result.route);
    onClose();
  }, [navigate, onClose]);

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={null}
      size="lg"
      withCloseButton={false}
      padding={0}
      styles={{
        body: { padding: 0 },
        header: { display: 'none' },
      }}
    >
      <TextInput
        placeholder="Search projects, people, shelters, tickets..."
        leftSection={<IconSearch size={18} />}
        rightSection={<Kbd size="xs">Esc</Kbd>}
        value={query}
        onChange={e => setQuery(e.currentTarget.value)}
        size="lg"
        autoFocus
        styles={{
          input: { border: 'none', borderBottom: '1px solid var(--mantine-color-gray-3)', borderRadius: 0 },
        }}
      />

      <div style={{ maxHeight: 400, overflowY: 'auto', padding: query.length >= 2 ? 8 : 0 }}>
        {query.length >= 2 && results.length === 0 && (
          <Text size="sm" c="dimmed" ta="center" py="lg">No results for "{query}"</Text>
        )}

        <Stack gap={2}>
          {results.map((r, i) => (
            <Paper key={i} p="sm" radius="sm"
              style={{ cursor: 'pointer' }}
              onClick={() => handleSelect(r)}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--mantine-color-gray-0)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = ''; }}
            >
              <Group justify="space-between">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Group gap={8}>
                    <Badge size="xs" variant="light" color={r.color}>{r.type}</Badge>
                    <Text size="sm" fw={500} truncate>{r.label}</Text>
                  </Group>
                  {r.detail && <Text size="xs" c="dimmed" truncate>{r.detail}</Text>}
                </div>
              </Group>
            </Paper>
          ))}
        </Stack>
      </div>
    </Modal>
  );
}

export { GlobalSearch };
